// Szervezeti felépítés (org-structure) — admin-oldal, HÁROM fül a szervezeti fa szintjei szerint:
// Részlegek → Csapatok → Beosztások. A nézet-váltó a `?tab=` URL-search-paramban perzisztál (az assets
// „Leltár/Igénylések" mintája — megosztható/visszaállítható link). A három fül három önálló erőforrás
// (workforce.departments / workforce.teams / workforce.positions) → három önálló panel, saját keresővel,
// rendezéssel, lapozással és CRUD-dal. Ez a route csak a fül-állapotot és a fül-váltást vezérli (a page-gate
// `organizational_structure.view`-t maguk a panelek végzik — a BFF is 403-at ad jog nélkül).
//
// Deep-link: `?tab=<departments|teams|positions>&edit=<id>` → az adott fül szerkesztő-sheetje a megadott
// entitásra nyílik (a command palette „2. rungja" + a globális kereső cél-útja). A `tab` választja ki a panelt,
// az `edit` a benne kinyíló sort; záráskor az `edit` törlődik (a `tab` marad) → back-barát + újra-nyitható.
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { z } from 'zod';

import { DepartmentsPanel } from '@/components/org-structure/departments-panel';
import { type OrgTab } from '@/components/org-structure/org-structure-tabs';
import { PositionsPanel } from '@/components/org-structure/positions-panel';
import { TeamsPanel } from '@/components/org-structure/teams-panel';
import { requireAuth } from '@/lib/auth';

const orgStructureSearchSchema = z.object({
  tab: z.enum(['departments', 'teams', 'positions']).catch('departments'),
  edit: z.string().optional(),
});

export const Route = createFileRoute('/org-structure')({
  validateSearch: (search) => orgStructureSearchSchema.parse(search),
  beforeLoad: () => requireAuth(),
  component: OrgStructurePage,
});

function OrgStructurePage(): JSX.Element {
  const { tab, edit } = Route.useSearch();
  const navigate = useNavigate();

  // Fül-váltás → URL-be írjuk (replace — a fül-váltás ne szemetelje a historyt). A fül-váltás egyben az `edit`
  // deep-linket is ejti (a másik fül entitására nem érvényes).
  const switchTab = (v: string): void => {
    const next: OrgTab = v === 'positions' ? 'positions' : v === 'teams' ? 'teams' : 'departments';
    if (next === tab) return;
    void navigate({ to: '/org-structure', search: { tab: next }, replace: true });
  };

  // Deep-link param-törlés (a sheet záráskor) — a `tab` marad, csak az `edit` ürül.
  const clearDeepLink = useCallback(() => {
    void navigate({ to: '/org-structure', search: { tab, edit: undefined }, replace: true });
  }, [navigate, tab]);

  const panelProps = {
    tab,
    onTabChange: switchTab,
    deepLinkEditId: edit,
    onClearDeepLink: clearDeepLink,
  };

  if (tab === 'positions') return <PositionsPanel {...panelProps} />;
  if (tab === 'teams') return <TeamsPanel {...panelProps} />;
  return <DepartmentsPanel {...panelProps} />;
}
